/**
 * Which workspace the user is in: personal (null) or one business (org id).
 *
 * Small zustand store. The selection is remembered per user in localStorage (every
 * access wrapped in try/catch, the store works without it). Nothing here talks to the
 * network, so the API client can import it without a cycle.
 */
import { create } from 'zustand';
import { isOrgsEnabled } from './flag';

export const WORKSPACE_STORAGE_PREFIX = 'andi.workspace.v1.';

function readStored(userId: string): string | null {
  try {
    const value = window.localStorage.getItem(WORKSPACE_STORAGE_PREFIX + userId);
    return value && value.length > 0 ? value : null;
  } catch {
    return null;
  }
}

function writeStored(userId: string | null, orgId: string | null): void {
  if (!userId) return;
  try {
    if (orgId) {
      window.localStorage.setItem(WORKSPACE_STORAGE_PREFIX + userId, orgId);
    } else {
      window.localStorage.removeItem(WORKSPACE_STORAGE_PREFIX + userId);
    }
  } catch {
    // Storage blocked or full: the selection simply is not remembered.
  }
}

interface WorkspaceState {
  /** The signed in user the selection belongs to. */
  userId: string | null;
  /** Selected business, or null for Personal. */
  activeOrgId: string | null;
  /** True once the server answered 404 not_enabled: the feature is off for everyone. */
  serverDisabled: boolean;
  /** Load the remembered selection for a user (no change when the flag is off). */
  load: (userId: string | null) => void;
  /** Switch workspace. null means Personal. */
  select: (orgId: string | null) => void;
  /** Drop the selection when that business is not one of the user's memberships. */
  reconcile: (memberOrgIds: readonly string[]) => void;
  markServerDisabled: () => void;
}

export const useWorkspaceStore = create<WorkspaceState>((set, get) => ({
  userId: null,
  activeOrgId: null,
  serverDisabled: false,
  load: (userId) => {
    if (!isOrgsEnabled() || !userId) {
      set({ userId, activeOrgId: null });
      return;
    }
    set({ userId, activeOrgId: readStored(userId) });
  },
  select: (orgId) => {
    writeStored(get().userId, orgId);
    set({ activeOrgId: orgId });
  },
  reconcile: (memberOrgIds) => {
    const { activeOrgId, userId } = get();
    if (activeOrgId && !memberOrgIds.includes(activeOrgId)) {
      writeStored(userId, null);
      set({ activeOrgId: null });
    }
  },
  markServerDisabled: () => {
    writeStored(get().userId, null);
    set({ serverDisabled: true, activeOrgId: null });
  },
}));

/** The org id to send as X-Org-ID, or null. Always null with the feature off. */
export function getActiveOrgId(): string | null {
  if (!isOrgsEnabled()) return null;
  const { activeOrgId, serverDisabled } = useWorkspaceStore.getState();
  return serverDisabled ? null : activeOrgId;
}
